import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Sparkles, Calendar, TrendingUp, AlertCircle, Package,
  Users, BadgePercent, RefreshCw, Wand2
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmt = v => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(v || 0);

const FESTIVAL_EMOJI = {
  holi: 'Holi',
  raksha_bandhan: 'Raksha Bandhan',
  ganesh_chaturthi: 'Ganesh Chaturthi',
  navratri: 'Navratri',
  diwali: 'Diwali',
  christmas: 'Christmas',
};

export default function FestivePlanner() {
  const [festivals, setFestivals] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [plan, setPlan] = useState(null);
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planLoading, setPlanLoading] = useState(false);
  const [briefLoading, setBriefLoading] = useState(false);

  const loadFestivals = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/festive/upcoming`, {
        params: { horizon_days: 365 },
        withCredentials: true,
      });
      const list = res.data?.festivals || [];
      setFestivals(list);
      if (list.length > 0) setSelectedKey(list[0].key);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load festivals');
    } finally { setLoading(false); }
  };

  const loadPlan = async (key) => {
    if (!key) return;
    setPlanLoading(true);
    setBrief(null);
    try {
      const res = await axios.get(`${API}/festive/plan/${key}`, { withCredentials: true });
      setPlan(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load plan');
    } finally { setPlanLoading(false); }
  };

  const generateBrief = async () => {
    if (!selectedKey) return;
    setBriefLoading(true);
    try {
      const res = await axios.post(`${API}/festive/plan/${selectedKey}/ai-brief`, {}, { withCredentials: true });
      setBrief(res.data?.narrative || '');
      toast.success('Sales brief generated');
    } catch (err) {
      toast.error('Brief generation failed');
    } finally { setBriefLoading(false); }
  };

  useEffect(() => { loadFestivals(); }, []);
  useEffect(() => { if (selectedKey) loadPlan(selectedKey); }, [selectedKey]);

  return (
    <div data-testid="festive-page" className="p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-zinc-100 flex items-center gap-2">
            <Sparkles className="text-pink-400" size={24} /> Festive Campaign Planner
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            Last-year demand patterns → production schedule + client targeting + discount bands.
          </p>
        </div>
        <button
          data-testid="festive-refresh"
          onClick={loadFestivals}
          className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700"
        ><RefreshCw size={14} /></button>
      </div>

      {/* Festival chips */}
      {loading ? (
        <div className="text-sm text-zinc-500 animate-pulse">Loading upcoming festivals…</div>
      ) : festivals.length === 0 ? (
        <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-8 text-center text-zinc-500 text-sm">
          No festivals in the next 12 months. Check back later.
        </div>
      ) : (
        <div className="flex gap-2 flex-wrap" data-testid="festive-chips">
          {festivals.map(f => (
            <button
              key={f.key}
              data-testid={`festive-chip-${f.key}`}
              onClick={() => setSelectedKey(f.key)}
              className={`px-4 py-2.5 rounded-xl border text-left transition-all ${
                selectedKey === f.key
                  ? 'bg-pink-600/20 border-pink-500 text-pink-100'
                  : 'bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-zinc-700'
              }`}
            >
              <div className="text-sm font-bold flex items-center gap-1.5">
                <Calendar size={12} /> {FESTIVAL_EMOJI[f.key] || f.name}
              </div>
              <div className="text-[11px] text-zinc-400 mt-0.5">
                {f.date} · <span className="font-semibold">{f.days_away}d away</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Plan */}
      {selectedKey && (
        planLoading ? (
          <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-8 text-center text-zinc-500 text-sm animate-pulse">
            Analyzing last-year dispatches…
          </div>
        ) : plan ? (
          <>
            {/* Plan header */}
            <div className="bg-gradient-to-r from-pink-900/30 via-rose-900/20 to-orange-900/20 border border-pink-700/40 rounded-xl p-5">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="text-pink-300 text-sm font-medium">{plan.festival.name}</div>
                  <div className="text-2xl font-bold text-zinc-100">{plan.festival.date}</div>
                  <div className="text-xs text-zinc-400 mt-1">
                    {plan.festival.days_away} days away · Prep must start by <span className="text-amber-300 font-semibold">{plan.prep_start}</span>
                  </div>
                </div>
                <button
                  data-testid="festive-ai-brief"
                  onClick={generateBrief}
                  disabled={briefLoading}
                  className="px-4 py-2 rounded-lg bg-pink-600 hover:bg-pink-500 disabled:bg-zinc-700 text-white text-xs font-medium flex items-center gap-1.5"
                >
                  <Wand2 size={13} /> {briefLoading ? 'Generating…' : 'AI Sales Brief'}
                </button>
              </div>
            </div>

            {/* KPI tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KPI label="Products w/ history" value={plan.kpi.total_products} testid="fp-kpi-prod" />
              <KPI label="Last-year festive qty" value={fmt(plan.kpi.total_lift_qty)} testid="fp-kpi-lift" />
              <KPI label="Shortfall to produce" value={fmt(plan.kpi.total_shortfall)} color="text-amber-400" testid="fp-kpi-short" />
              <KPI label="Zero-stock risks" value={plan.kpi.zero_stock_count} color="text-red-400" testid="fp-kpi-risk" />
            </div>

            {/* AI brief (if generated) */}
            {brief && (
              <div data-testid="festive-brief-panel" className="bg-zinc-900/70 border border-pink-700/40 rounded-xl p-4">
                <div className="text-sm font-semibold text-zinc-100 mb-2 flex items-center gap-2">
                  <Wand2 size={14} className="text-pink-400" /> AI Sales Brief
                </div>
                <div className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">{brief}</div>
              </div>
            )}

            {/* Items */}
            {plan.items.length === 0 ? (
              <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-8 text-center text-zinc-500 text-sm">
                <AlertCircle size={20} className="mx-auto mb-2 text-zinc-600" />
                No last-year dispatch history for this festival window.
                <div className="text-xs mt-2">Run another festival cycle first, or proceed using gut-feel for now.</div>
              </div>
            ) : (
              <div className="space-y-3">
                {plan.items.map((it, i) => (
                  <div
                    key={i}
                    data-testid={`festive-item-${i}`}
                    className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-4 grid grid-cols-1 md:grid-cols-6 gap-3 items-start"
                  >
                    <div className="md:col-span-2">
                      <div className="flex items-center gap-2 mb-1">
                        <Package size={14} className="text-zinc-500" />
                        <div className="font-bold text-zinc-100">{it.product}</div>
                      </div>
                      <div className="text-[11px] text-zinc-500">₹{fmt(it.price)} / {it.unit}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-zinc-500 uppercase">Target</div>
                      <div className="text-lg font-bold text-zinc-100">{fmt(it.suggested_target_qty)} <span className="text-[10px] text-zinc-500">{it.unit}</span></div>
                      {it.lift_pct !== null && (
                        <div className="text-[10px] text-emerald-400 flex items-center gap-0.5"><TrendingUp size={10} /> +{it.lift_pct}% lift</div>
                      )}
                    </div>
                    <div>
                      <div className="text-[10px] text-zinc-500 uppercase">Current Stock</div>
                      <div className={`text-lg font-bold ${it.current_stock > 0 ? 'text-zinc-100' : 'text-red-400'}`}>{fmt(it.current_stock)}</div>
                      {it.shortfall_to_produce > 0 && (
                        <div className="text-[10px] text-amber-400">need +{fmt(it.shortfall_to_produce)}</div>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <div className="text-[10px] text-zinc-500 uppercase mb-1 flex items-center gap-1"><Users size={10} /> Top clients</div>
                      {it.top_clients?.length > 0 ? it.top_clients.map((c, j) => (
                        <div key={j} className="text-xs text-zinc-300 truncate">{j + 1}. {c.name} <span className="text-zinc-500">({fmt(c.qty)})</span></div>
                      )) : <div className="text-xs text-zinc-600">—</div>}
                      <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] border bg-violet-900/30 text-violet-300 border-violet-700">
                        <BadgePercent size={10} /> Discount: {it.discount.band}
                      </div>
                      <div className="text-[10px] text-zinc-500 mt-1 italic">{it.discount.rationale}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : null
      )}
    </div>
  );
}

const KPI = ({ label, value, testid, color }) => (
  <div data-testid={testid} className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-4">
    <div className="text-xs text-zinc-400">{label}</div>
    <div className={`text-2xl font-bold mt-1 ${color || 'text-zinc-100'}`}>{value}</div>
  </div>
);
